import { useEffect, useMemo, useState } from "react"
import { Box, Button, IconButton, Stack, Typography } from "@mui/material"
import { RightDrawer } from "lib/@components/drawer/right-drawer"
import { Repeat } from "lib/@components/repeat"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import Iconify from "minimals-template/components/Iconify"
import { ruleSets as ruleSetsApi } from "routes/rule-sets/controller/rule-sets"
import { RulesPanelAppliedList } from "./rules-panel-applied-list"
import { RulesPanelRuleSetOption } from "./rules-panel-rule-set-option"

export function RulesPanel({
    open,
    onClose,
    ruleSets,
    appliedRuleSetIds,
    onAddRuleSets,
    onCreateRuleSet,
    onEditRuleSet,
    onRemoveRuleSet,
    onSaveRuleSetOrder,
}) {
    const [selectingRuleSets, setSelectingRuleSets] = useState(false)
    const [selectedRuleSetIds, setSelectedRuleSetIds] = useState([])
    const [orderDirty, setOrderDirty] = useState(false)
    const availableRuleSetResult = ruleSetsApi.useResults(
        selectingRuleSets ? { skip: 0, take: 100, sort: "name", descending: false, status: "active" } : null
    )
    const availableRuleSets = useMemo(getAvailableRuleSets, [availableRuleSetResult, appliedRuleSetIds])
    const selectedIds = useMemo(getSelectedIds, [selectedRuleSetIds])
    const ruleSetCount = ruleSets?.length ?? 0
    const canAddRuleSets = typeof onAddRuleSets === "function"
    const canCreateRuleSet = typeof onCreateRuleSet === "function"

    useEffect(resetSelectionWhenClosed, [open])

    return (
        <RightDrawer open={open} setOpen={handleSetOpen} onClose={onClose}>
            <If value={selectingRuleSets}>
                <Stack data-cy="rules-panel-rule-set-picker" spacing={2} sx={{ height: "100%" }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0 }}>
                            <IconButton
                                aria-label="Back"
                                data-cy="rules-panel-rule-set-picker-back"
                                size="small"
                                onClick={handleCancelRuleSetSelection}
                                sx={{ mt: -0.25 }}
                            >
                                <Iconify icon="eva:arrow-ios-back-fill" />
                            </IconButton>
                            <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                                <Typography variant="h6">Add Rule Sets</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Choose existing rule sets to apply to this scenario.
                                </Typography>
                            </Stack>
                        </Stack>
                        <IconButton aria-label="Close" size="small" onClick={onClose} data-cy="rules-panel-close">
                            <Iconify icon="eva:close-fill" />
                        </IconButton>
                    </Stack>

                    <If value={canCreateRuleSet}>
                        <Button
                            data-cy="rules-panel-create-rule-set"
                            variant="outlined"
                            startIcon={<Iconify icon="mdi:plus" />}
                            onClick={handleCreateRuleSet}
                            sx={{ justifyContent: "flex-start" }}
                        >
                            Create new rule set
                        </Button>
                    </If>

                    <If value={availableRuleSets.length === 0}>
                        <Box sx={{ py: 4, textAlign: "center" }}>
                            <Iconify
                                icon="mdi:filter-variant-remove"
                                sx={{ fontSize: 40, color: "text.disabled", mb: 1 }}
                            />
                            <Typography variant="body2" color="text.secondary">
                                No rule sets available to add.
                            </Typography>
                        </Box>
                        <Else>
                            <Stack spacing={1} sx={{ flex: 1, overflow: "auto" }}>
                                <Repeat
                                    list={availableRuleSets}
                                    item={
                                        <RulesPanelRuleSetOption
                                            selectedIds={selectedIds}
                                            onToggle={handleToggleRuleSet}
                                        />
                                    }
                                />
                            </Stack>
                        </Else>
                    </If>

                    <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                        <Button onClick={handleCancelRuleSetSelection}>Cancel</Button>
                        <Button
                            data-cy="rules-panel-add-selected-rule-sets"
                            variant="contained"
                            disabled={selectedRuleSetIds.length === 0}
                            onClick={handleAddSelectedRuleSets}
                        >
                            Add Rule Sets
                        </Button>
                    </Stack>
                </Stack>
                <Else>
                    <Stack data-cy="rules-panel" spacing={2} sx={{ height: "100%" }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                            <Stack spacing={0.5}>
                                <Typography variant="h6">Rule Sets</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {formatAppliedRuleSetCount()}
                                </Typography>
                            </Stack>
                            <IconButton aria-label="Close" size="small" onClick={onClose} data-cy="rules-panel-close">
                                <Iconify icon="eva:close-fill" />
                            </IconButton>
                        </Stack>

                        <If value={canAddRuleSets}>
                            <Button
                                data-cy="rules-panel-add-btn"
                                size="small"
                                variant="outlined"
                                startIcon={<Iconify icon="mdi:plus" />}
                                onClick={handleStartRuleSetSelection}
                                disabled={orderDirty}
                            >
                                Add Rule Sets
                            </Button>
                        </If>

                        <If value={ruleSetCount === 0}>
                            <Box sx={{ py: 4, textAlign: "center" }}>
                                <Iconify
                                    icon="mdi:filter-variant-remove"
                                    sx={{ fontSize: 40, color: "text.disabled", mb: 1 }}
                                />
                                <Typography variant="body2" color="text.secondary">
                                    No rule sets applied yet.
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    No rule sets are attached to this scenario.
                                </Typography>
                            </Box>
                            <Else>
                                <RulesPanelAppliedList
                                    ruleSets={ruleSets || []}
                                    appliedRuleSetIds={appliedRuleSetIds || []}
                                    onEditRuleSet={onEditRuleSet}
                                    onRemoveRuleSet={onRemoveRuleSet}
                                    onSaveOrder={onSaveRuleSetOrder}
                                    onOrderDirtyChange={setOrderDirty}
                                />
                            </Else>
                        </If>
                    </Stack>
                </Else>
            </If>
        </RightDrawer>
    )

    function getAvailableRuleSets() {
        const appliedIds = new Set(appliedRuleSetIds || [])
        return (availableRuleSetResult?.items ?? []).filter((ruleSet) => !appliedIds.has(ruleSet.id))
    }

    function getSelectedIds() {
        return new Set(selectedRuleSetIds)
    }

    function resetSelectionWhenClosed() {
        if (open) return
        resetRuleSetSelection()
    }

    function handleSetOpen(value) {
        if (!value) {
            resetRuleSetSelection()
            onClose?.()
        }
    }

    function handleStartRuleSetSelection() {
        setSelectingRuleSets(true)
    }

    function handleCancelRuleSetSelection() {
        resetRuleSetSelection()
    }

    async function handleCreateRuleSet() {
        const created = await onCreateRuleSet?.()
        if (!created) return
        resetRuleSetSelection()
    }

    function handleToggleRuleSet(ruleSetId) {
        setSelectedRuleSetIds((previous) => toggleRuleSetId(previous, ruleSetId))
    }

    async function handleAddSelectedRuleSets() {
        const selectedRuleSets = availableRuleSets.filter(isSelectedRuleSet)
        if (!selectedRuleSets.length) return
        resetRuleSetSelection()
        await onAddRuleSets?.(selectedRuleSets)
    }

    function isSelectedRuleSet(ruleSet) {
        return selectedIds.has(ruleSet.id)
    }

    function resetRuleSetSelection() {
        setSelectingRuleSets(false)
        setSelectedRuleSetIds([])
    }

    function toggleRuleSetId(previous, ruleSetId) {
        if (previous.includes(ruleSetId)) {
            return previous.filter((id) => id !== ruleSetId)
        }
        return [...previous, ruleSetId]
    }

    function formatAppliedRuleSetCount() {
        if (ruleSetCount === 1) return "1 rule set applied"
        return `${ruleSetCount} rule sets applied`
    }
}
